import React, { useEffect, useRef, useState } from 'react';
import { Redirect, useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import i18next from '../../../i18n';
import styles from './ProductListingPage.module.scss';
import AppPaths from '../../AppPaths';
import { getDomainConfig } from '../../lib/domainConfig';
import { series3PlusCollarId } from '../../types/Product';
import { productListingPage, quizQuestionPage } from '../../lib/analytics/events';
import FiMiniImage from '../../assets/images/plp-fi-mini.png';
import FiSeries3PlusImage from '../../assets/images/plp-fi-series-three-plus.png';
import FiUltraImage from '../../assets/images/plp-fi-ultra.png';
import FiMiniForCatsImage from '../../assets/images/plp-fi-mini-cats.png';
import FiAccessoriesImage from '../../assets/images/plp-accessories.png';
import FiSupplementsImage from '../../assets/images/plp-supplements.png';
import FiNanoMicrochipsImage from '../../assets/images/plp-nano.png';
import { ReactComponent as DownArrow } from '../../assets/images/icons/arrow_down.svg';
import { ReactComponent as UpArrow } from '../../assets/images/icons/arrow_up.svg';
import { ReactComponent as Check } from '../../assets/images/check.svg';
import { ReactComponent as Dog } from '../../assets/images/icons/dog.svg';
import { ReactComponent as Cat } from '../../assets/images/icons/cat.svg';
import classNames from 'classnames';
import { collarProductIsSupportedInCountry, useLocale } from '../../contexts/LocaleContext';
import * as types from '../../types';
import { Button, Flex, Text } from '@mantine/core';
import { SaleBadge } from '../SaleBadge/SaleBadge';
import { getFiGrowthBook, useIsGrowthBookEnabled } from '../../lib/growthbook';
import FreeTrialCard from '../FreeTrialCard/FreeTrialCard';

type PlpCardLabel = string;

function getPlpCardLabel(title: string, subtitle?: string): PlpCardLabel {
  const series3PlusTitle = i18next.t('series_3_plus');
  const miniTitle = i18next.t('mini');
  const forCatsSubtitle = i18next.t('for_cats');

  if (title === series3PlusTitle) return i18next.t('series_3_plus');
  if (title === miniTitle && subtitle && subtitle.toUpperCase().includes(forCatsSubtitle.toUpperCase()))
    return i18next.t('mini_cats');
  return i18next.t('mini');
}

function trackDetailsToggle(title: string, subtitle: string | undefined, expanded: boolean) {
  const label = getPlpCardLabel(title, subtitle);
  const miniLabel = i18next.t('mini');
  const series3PlusLabel = i18next.t('series_3_plus');
  const miniCatsLabel = i18next.t('mini_cats');

  if (label === miniLabel) {
    expanded ? productListingPage.miniDetailsExpand() : productListingPage.miniDetailsCollapse();
  } else if (label === series3PlusLabel) {
    expanded ? productListingPage.series3PlusDetailsExpand() : productListingPage.series3PlusDetailsCollapse();
  } else if (label === miniCatsLabel) {
    expanded ? productListingPage.miniCatsDetailsExpand() : productListingPage.miniCatsDetailsCollapse();
  }
}

type BetterForProps = { children: React.ReactNode; color: string; icon: string };
const ProductCardBetterFor: React.FC<BetterForProps> = ({ children, color, icon }) => (
  <span className={classNames(styles.betterForItem, styles[color])}>
    {icon === 'dog' ? <Dog /> : <Cat />}
    {children}
  </span>
);

type CardProps = {
  title: string;
  subtitle?: string;
  description: string;
  image: string;
  cta: string;
  onClick: () => void;
  hardware?: string[];
  betterFor?: { color: string; label: string; icon: string }[];
  showSaleBadge?: boolean;
  freeTrialAvailable?: boolean;
};

const ProductCard: React.FC<CardProps> = ({
  title,
  subtitle,
  description,
  image,
  cta,
  onClick,
  hardware = [],
  betterFor = [],
  showSaleBadge = false,
  freeTrialAvailable = false,
}) => {
  const { t } = useTranslation();
  const [showDetails, setShowDetails] = useState(false);
  const hasDetails = hardware.length > 0 || betterFor.length > 0;

  return (
    <div className={styles.productCard}>
      <img className={styles.cardImage} src={image} alt={title} />
      <div className={styles.cardBody}>
        <div className={styles.cardTitle}>
          {title}
          <div className={styles.cardTitleSubtitle}>{subtitle}</div>
          {showSaleBadge && <SaleBadge className={styles.saleBadge} />}
        </div>
        <div className={styles.cardDescription}>{description}</div>
        <div className={styles.cardButtonRow}>
          <button className={styles.cardButton} onClick={onClick}>
            {cta}
          </button>
          {freeTrialAvailable && <div className={styles.freeTrialAvailableText}>{t('free_trial')}</div>}
        </div>
        {hasDetails && (
          <>
            <div className={styles.divider} />
            <button
              type="button"
              className={styles.detailsToggle}
              aria-expanded={showDetails}
              onClick={() =>
                setShowDetails((s) => {
                  const next = !s;
                  trackDetailsToggle(title, subtitle, next);
                  return next;
                })
              }
            >
              <div className={styles.closeContainer}>
                <span className={styles.closeContainerTextMobile}>{t('hide_details')}</span>
                <span className={styles.closeContainerTextDesktop}>{t('details')}</span>
                <UpArrow />
              </div>
              <div className={styles.openContainer}>
                <span className={styles.openContainerTextMobile}>{t('see_details')}</span>
                <span className={styles.openContainerTextDesktop}>{t('details')}</span>
                <DownArrow />
              </div>
            </button>

            <div className={classNames(styles.detailsSection, { [styles.open]: showDetails })}>
              <div className={styles.hardwareTitle}>{t('hardware_no_colon')}</div>
              <div className={styles.hardware}>
                {hardware.map((h) => (
                  <div className={styles.hardwareItem} key={h}>
                    <Check />
                    {h}
                  </div>
                ))}
              </div>
              <div className={styles.betterForTitle}>{t('better_for')}</div>
              <div className={styles.betterFor}>
                {betterFor.map((a) => (
                  <ProductCardBetterFor key={a.label} color={a.color} icon={a.icon}>
                    {a.label}
                  </ProductCardBetterFor>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const ProductCarousel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className={styles.carousel}>{children}</div>
);

const ProductListingPage: React.FC = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const scrollFiredRef = useRef<{ [k: string]: boolean }>({ '50': false, '75': false, '100': false });
  const { country } = useLocale();
  const isUltraExperienceEnabled = useIsGrowthBookEnabled()
    ? getFiGrowthBook().getFeatureValue<boolean>('ultra-experience', false)
    : false;
  const isFreeTrialEnabled = useIsGrowthBookEnabled()
    ? getFiGrowthBook().getFeatureValue<boolean>('free-trial-enabled', false)
    : false;

  useEffect(() => {
    productListingPage.pageViewed();

    const onScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop || 0;
      const height = (document.documentElement.scrollHeight || 0) - window.innerHeight;
      if (height <= 0) {
        return;
      }
      const pct = (scrollTop / height) * 100;
      if (!scrollFiredRef.current['50'] && pct >= 50) {
        productListingPage.scroll50();
        scrollFiredRef.current['50'] = true;
      }
      if (!scrollFiredRef.current['75'] && pct >= 75) {
        productListingPage.scroll75();
        scrollFiredRef.current['75'] = true;
      }
      if (!scrollFiredRef.current['100'] && pct >= 100) {
        productListingPage.scroll100();
        scrollFiredRef.current['100'] = true;
      }
    };

    window.addEventListener('scroll', onScroll, { passive: true } as any);
    onScroll();
    return () => window.removeEventListener('scroll', onScroll as any);
  }, []);

  if (!collarProductIsSupportedInCountry(types.miniCollarId, country)) {
    return <Redirect to={AppPaths.Products(types.series3PlusCollarId)} />;
  }

  const miniBatteryLife = t('up_to_6_weeks_battery_life');

  return (
    <div className={classNames('product-listing-page', styles.container)}>
      <Flex direction={'column'} gap={16} align="center">
        <Text size={56} fw={700} lh={1} sx={{ letterSpacing: '-1.12px' }}>
          {t('trusted_by_millions_of_pet_parents')}
        </Text>
        <Text
          size={20}
          fw={700}
          lh={1.2}
          sx={(theme) => ({ letterSpacing: '-0.4px', color: theme.colors['type-light-secondary'] })}
        >
          {t('most_advanced_gps_tracking_solutions_for_your_sidekick')}
        </Text>
        <Button
          bg={'black'}
          radius={100}
          sx={{
            '&:hover, &:focus, &:active': { background: 'black' },
          }}
          onClick={() => {
            quizQuestionPage.quizButtonTapped();
            history.push(AppPaths.Quiz);
          }}
        >
          <Text size={14} fw={600} lh={1.2} py={10} px={24} sx={{ letterSpacing: '-0.14px' }}>
            {t('which_fi_is_right_for_me')}
          </Text>
        </Button>
      </Flex>
      <ProductCarousel>
        <ProductCard
          title="Series 3+"
          description="High-performance GPS and AI-driven insights in a durable, integrated design."
          image={FiSeries3PlusImage}
          cta={t('shop_fi_series_3_plus')}
          onClick={() => {
            productListingPage.series3PlusShopNow();
            history.push(AppPaths.Products(series3PlusCollarId));
          }}
          hardware={[t('high_durability'), t('built_directly_into_the_collar'), t('up_to_3_month_of_battery_life')]}
          betterFor={[
            { color: 'blue', label: t('medium_dogs'), icon: 'dog' },
            { color: 'green', label: t('large_dogs'), icon: 'dog' },
            { color: 'green', label: t('extra_large_dogs'), icon: 'dog' },
          ]}
          showSaleBadge={true}
          freeTrialAvailable={isFreeTrialEnabled}
        />
        <ProductCard
          title="Mini"
          description="Lightweight, attachable GPS and health tracker for small dogs."
          image={FiMiniImage}
          cta={t('shop_fi_mini')}
          onClick={() => {
            productListingPage.miniShopNow();
            history.push(AppPaths.Products('fi-mini') + '?type=dog');
          }}
          hardware={[t('attachable_to_collars_and_harnesses'), t('small_and_lightweight'), miniBatteryLife]}
          betterFor={[
            { color: 'yellow', label: t('small_dogs'), icon: 'dog' },
            { color: 'yellow', label: t('puppies'), icon: 'dog' },
            { color: 'blue', label: t('medium_dogs'), icon: 'dog' },
          ]}
          showSaleBadge={true}
          freeTrialAvailable={isFreeTrialEnabled}
        />
        {isUltraExperienceEnabled && (
          <ProductCard
            title="Ultra"
            description="Ultra-rugged Starlink GPS tracker for any adventure, any collar, anywhere."
            image={FiUltraImage}
            cta={t('shop_fi_ultra')}
            onClick={() => {
              // TODO Add Ultra PDP
            }}
            hardware={[
              t('attachable_to_collars_and_harnesses'),
              t('satellite_tracking_with_starlink'),
              t('sound_and_vibration_feedback'),
            ]}
            betterFor={[
              { color: 'blue', label: t('medium_dogs'), icon: 'dog' },
              { color: 'green', label: t('large_dogs'), icon: 'dog' },
              { color: 'green', label: t('extra_large_dogs'), icon: 'dog' },
            ]}
            showSaleBadge={true}
            freeTrialAvailable={isFreeTrialEnabled}
          />
        )}
        <ProductCard
          title="Mini"
          subtitle="FOR CATS"
          description="Feather-light and compact attachable GPS tracker for indoor or outdoor cats."
          image={FiMiniForCatsImage}
          cta={t('shop_fi_mini')}
          onClick={() => {
            productListingPage.miniCatsShopNow();
            history.push(AppPaths.Products('fi-mini') + '?type=cat');
          }}
          hardware={[t('attachable_to_collars_and_harnesses'), t('small_and_lightweight'), miniBatteryLife]}
          betterFor={[{ color: 'yellow', label: t('cats'), icon: 'cat' }]}
          showSaleBadge={true}
          freeTrialAvailable={isFreeTrialEnabled}
        />
      </ProductCarousel>

      <div className={styles.compareBox}>
        <div className={styles.compareTitle}>{t('not_sure_which_fi_is_for_you')}</div>
        <div className={styles.compareButtonsContainer}>
          <button
            className={styles.helpMeChooseCta}
            onClick={() => {
              quizQuestionPage.quizButtonTapped();
              history.push(AppPaths.Quiz);
            }}
          >
            {t('help_me_choose')}
          </button>
          <button
            className={styles.compareCta}
            onClick={() => {
              productListingPage.comparisonCompareProducts();
              history.push(AppPaths.Compare);
            }}
          >
            {t('compare_products')}
          </button>
        </div>
      </div>

      {isFreeTrialEnabled && (
        <FreeTrialCard
          onStartFreeTrial={() => {
            productListingPage.freeTrialStartFreeTrial();
            history.push(AppPaths.Products(series3PlusCollarId));
          }}
        />
      )}

      <div className={styles.keepShoppingTitle}>{t('keep_shopping')}</div>
      <ProductCarousel>
        <ProductCard
          title={t('accessories')}
          description={t('shop_bases_collars_attachments_and_more')}
          image={FiAccessoriesImage}
          cta={t('shop_all')}
          onClick={() => {
            productListingPage.keepShoppingAccessories();
            history.push(AppPaths.Accessories);
          }}
        />
        <ProductCard
          title={t('supplements')}
          description={t('data_driven_formulas_for_daily_dog_care')}
          image={FiSupplementsImage}
          cta={t('shop_all')}
          onClick={() => {
            productListingPage.keepShoppingSupplements();
            history.push(AppPaths.Products('supplements'));
          }}
        />
        <ProductCard
          title={t('nano_microchips')}
          description={t('microchips_for_vets_and_rescue_shelters')}
          image={FiNanoMicrochipsImage}
          cta={t('shop_all')}
          onClick={() => {
            productListingPage.keepShoppingNano();
            window.location.href = `https://${getDomainConfig().nanoDomain}/`;
          }}
        />
      </ProductCarousel>
      <div className={styles.footer} />
    </div>
  );
};

export default ProductListingPage;
